import { charMaps as C, applyMap, combine, flipText, reverseText, zalgo, between, wrapEach, alternate, spaceText, M } from '../lib/unicode';

const map = (fn) => (t) => applyMap(t, fn);

// Home (cursive) styles - 18
export const cursiveStyles = [
  { id: 'cursive-bold', name: 'Cursive Bold', cat: 'cursive', badge: 'POP', fn: map(C.boldScript) },
  { id: 'cursive-normal', name: 'Cursive Normal', cat: 'cursive', fn: map(C.script) },
  { id: 'italic-serif', name: 'Italic Serif', cat: 'cursive', fn: map(C.italic) },
  { id: 'bold-italic', name: 'Bold Italic', cat: 'bold', fn: map(C.boldItalic) },
  { id: 'double-struck', name: 'Double Struck', cat: 'special', badge: 'NEW', fn: map(C.doubleStruck) },
  { id: 'fraktur', name: 'Fraktur', cat: 'gothic', fn: map(C.fraktur) },
  { id: 'bold-fraktur', name: 'Bold Fraktur', cat: 'gothic', fn: map(C.boldFraktur) },
  { id: 'sans-bold', name: 'Sans Bold', cat: 'bold', fn: map(C.sansBold) },
  { id: 'sans-italic', name: 'Sans Italic', cat: 'cursive', fn: map(C.sansItalic) },
  { id: 'sans-bold-italic', name: 'Sans Bold Italic', cat: 'bold', badge: 'NEW', fn: map(C.sansBoldItalic) },
  { id: 'monospace', name: 'Monospace', cat: 'special', fn: map(C.monospace) },
  { id: 'small-caps', name: 'Small Caps', cat: 'special', fn: map(C.smallCaps) },
  { id: 'bubbles', name: 'Bubbles', cat: 'special', fn: map(C.circled) },
  { id: 'inverted', name: 'Inverted', cat: 'special', badge: 'NEW', fn: flipText },
  { id: 'strikethrough', name: 'Strikethrough', cat: 'special', fn: (t) => combine(t, M.strike) },
  { id: 'underline', name: 'Underline', cat: 'special', fn: (t) => combine(t, M.underline) },
  { id: 'wavy-underline', name: 'Wavy Underline', cat: 'special', badge: 'NEW', fn: (t) => combine(t, M.wavy) },
  { id: 'serif-bold', name: 'Serif Bold', cat: 'bold', fn: map(C.serifBold) },
];

export const cursiveFilters = [
  { id: 'all', label: 'All' },
  { id: 'cursive', label: 'Cursive' },
  { id: 'bold', label: 'Bold' },
  { id: 'gothic', label: 'Gothic' },
  { id: 'special', label: 'Special' },
];

// Weird text styles - 22 (display labels come from WEIRD_T[lang].cats)
export const weirdCategories = [
  { id: 'all' },
  { id: 'glitch' },
  { id: 'ancient' },
  { id: 'blocky' },
  { id: 'playful' },
  { id: 'math' },
];

export const weirdStyles = [
  { id: 'old-script', name: 'Old Inscription', cat: 'ancient', fn: (t) => applyMap(t.toUpperCase(), C.oldItalic) },
  { id: 'blackboard', name: 'Blackboard', cat: 'math', fn: map(C.doubleStruck) },
  { id: 'corners', name: 'Corner Brackets', cat: 'blocky', fn: (t) => wrapEach(t, '\u25de', '\u25dd') },
  { id: 'chaos', name: 'Chaos Mode', cat: 'glitch', fn: (t) => zalgo(t, 4) },
  { id: 'arrows', name: 'Up & Down', cat: 'playful', fn: (t) => wrapEach(t, '\u2e1b', '\u2e1c') },
  { id: 'snake', name: 'Snake Trail', cat: 'playful', fn: (t) => combine(t, M.tilde) },
  { id: 'cards', name: 'Card Suits', cat: 'playful', fn: (t) => { const s = ['\u2660', '\u2663', '\u2665', '\u2666']; let i = 0; return Array.from(t).map((c, k, a) => (c === ' ' || k === a.length - 1 || a[k + 1] === ' ' ? c : c + s[i++ % 4])).join(''); } },
  { id: 'vampire', name: 'Night Gothic', cat: 'glitch', fn: map(C.boldFraktur) },
  { id: 'corners-sc', name: 'Corner Caps', cat: 'blocky', fn: (t) => wrapEach(applyMap(t, C.smallCaps), '\u25de', '\u25dd') },
  { id: 'dotted-acronym', name: 'Dotted Caps', cat: 'glitch', fn: (t) => t.toUpperCase().split(' ').map((w) => Array.from(w).map((c) => c + M.short + '.').join('')).join(' ') },
  { id: 'upside', name: 'Flip Over', cat: 'glitch', fn: (t) => flipText(t.toLowerCase()) },
  { id: 'ghost', name: 'Ghost Ink', cat: 'glitch', fn: (t) => combine(t, M.sparkle) },
  { id: 'bubbles', name: 'Bubble Pop', cat: 'blocky', fn: map(C.circled) },
  { id: 'melt', name: 'Melting', cat: 'glitch', fn: (t) => combine(t, M.underline) + ' \ud83d\udca7' },
  { id: 'mock', name: 'Mocking Case', cat: 'playful', fn: alternate },
  { id: 'skulls', name: 'Bone Chain', cat: 'playful', fn: (t) => Array.from(t).join('\ud83d\udc80') },
  { id: 'tiles', name: 'Tile Board', cat: 'blocky', fn: map(C.squared) },
  { id: 'override', name: 'Slashed', cat: 'glitch', fn: (t) => combine(t, M.slash) },
  { id: 'wide-bricks', name: 'Brick Wall', cat: 'blocky', fn: (t) => t.split(' ').map((w) => applyMap(w, C.fullwidth)).join(' \ud83e\uddf1 ') },
  { id: 'runes', name: 'Rune Carving', cat: 'ancient', fn: map(C.runic) },
  { id: 'bars', name: 'Jail Cell', cat: 'glitch', fn: (t) => wrapEach(t, '\u2758', '\u2758') },
  { id: 'tux', name: 'Checkerboard', cat: 'blocky', fn: (t) => { let i = 0; return Array.from(t).map((c) => (/[a-z]/i.test(c) ? (i++ % 2 === 0 ? C.serifBold(c) : c) : c)).join(''); } },
];

// Discord styles (~80)
const frame = (l, r, inner) => (t) => `${l} ${inner(t)} ${r}`;
const mS = map(C.script), mBS = map(C.boldScript), mB = map(C.serifBold), mSB = map(C.sansBold), mBF = map(C.boldFraktur), mF = map(C.fraktur), mSI = map(C.sansItalic), mSans = map(C.sans), mMono = map(C.monospace), mFW = map(C.fullwidth), mDS = map(C.doubleStruck);

export const discordCategories = ['All', 'Bold', 'Italic', 'Script', 'Gothic', 'Decorative', 'Aesthetic', 'Edgy', 'Fun', 'Special', 'Clean'];

export const discordStyles = [
  { name: 'Bold', cat: 'Bold', hot: true, fn: mB },
  { name: 'Sans Bold', cat: 'Bold', fn: mSB },
  { name: 'Italic', cat: 'Italic', fn: map(C.italic) },
  { name: 'Bold Italic', cat: 'Italic', fn: map(C.boldItalic) },
  { name: 'Sans Italic', cat: 'Italic', fn: mSI },
  { name: 'Sans Bold Italic', cat: 'Italic', fn: map(C.sansBoldItalic) },
  { name: 'Script', cat: 'Script', hot: true, fn: mS },
  { name: 'Bold Script', cat: 'Script', hot: true, fn: mBS },
  { name: 'Fraktur', cat: 'Gothic', fn: mF },
  { name: 'Bold Fraktur', cat: 'Gothic', fn: mBF },
  { name: 'Outline', cat: 'Clean', fn: mDS },
  { name: 'Sans Serif', cat: 'Clean', fn: mSans },
  { name: 'Monospace', cat: 'Clean', fn: mMono },
  { name: 'Wide', cat: 'Aesthetic', hot: true, fn: mFW },
  { name: 'Bubbles', cat: 'Decorative', fn: map(C.circled) },
  { name: 'Filled Bubbles', cat: 'Decorative', fn: map(C.circledFilled) },
  { name: 'Squares', cat: 'Decorative', fn: map(C.squared) },
  { name: 'Filled Squares', cat: 'Decorative', fn: map(C.squaredFilled) },
  { name: 'Regional', cat: 'Fun', fn: map(C.regional) },
  { name: 'Small Caps', cat: 'Clean', fn: map(C.smallCaps) },
  { name: 'Superscript', cat: 'Special', fn: map(C.superscript) },
  { name: 'Subscript', cat: 'Special', fn: (t) => applyMap(t.toLowerCase(), C.subscript) },
  { name: 'Currency', cat: 'Edgy', fn: map(C.currency) },
  { name: 'Greek Style', cat: 'Edgy', fn: map(C.greek) },
  { name: 'Slavic', cat: 'Edgy', fn: map(C.slavic) },
  { name: 'Runic', cat: 'Edgy', fn: map(C.runic) },
  { name: 'Exotic', cat: 'Special', fn: map(C.exotic) },
  { name: 'Accented', cat: 'Special', fn: map(C.accented) },
  { name: 'Fancy Mix', cat: 'Edgy', fn: map(C.cherokee) },
  { name: 'Strikethrough', cat: 'Special', fn: (t) => combine(t, M.strike) },
  { name: 'Underline', cat: 'Special', fn: (t) => combine(t, M.underline) },
  { name: 'Wavy Underline', cat: 'Special', fn: (t) => combine(t, M.wavy) },
  { name: 'Slashed', cat: 'Edgy', fn: (t) => combine(t, M.slash) },
  { name: 'Stacked', cat: 'Special', fn: (t) => combine(t, M.stacked) },
  { name: 'Halo', cat: 'Special', fn: (t) => combine(t, M.halo) },
  { name: 'Crossed', cat: 'Edgy', fn: (t) => combine(t, M.crossed) },
  { name: 'Arrow Up', cat: 'Special', fn: (t) => combine(t, M.arrow) },
  { name: 'Upside Down', cat: 'Fun', fn: flipText },
  { name: 'Reversed', cat: 'Fun', fn: reverseText },
  { name: 'Spaced Out', cat: 'Aesthetic', fn: spaceText },
  { name: 'Vaporwave', cat: 'Aesthetic', hot: true, fn: (t) => Array.from(mFW(t)).join('\u3000') },
  { name: 'aLtErNaTiNg', cat: 'Fun', fn: alternate },
  { name: 'Glitch', cat: 'Fun', fn: (t) => zalgo(t, 1) },
  { name: 'Cursed', cat: 'Edgy', fn: (t) => zalgo(t, 2) },
  { name: 'Hearts', cat: 'Aesthetic', fn: frame('\u2661', '\u2661', mBS) },
  { name: 'Sparkles', cat: 'Aesthetic', hot: true, fn: frame('\u2727\uff65\uff9f: *', '* :\uff65\uff9f\u2727', mBS) },
  { name: 'Starlight', cat: 'Aesthetic', fn: frame('\u22c6\uff61\u00b0\u2729', '\u2729\u00b0\uff61\u22c6', mS) },
  { name: 'Royal', cat: 'Decorative', hot: true, fn: frame('\ua9c1\u0f3a', '\u0f3b\ua9c2', mBF) },
  { name: 'Flowers', cat: 'Aesthetic', fn: frame('\u273f\u2740', '\u2740\u273f', mS) },
  { name: 'Butterfly', cat: 'Aesthetic', fn: frame('\ud83e\udd8b', '\ud83e\udd8b', mBS) },
  { name: 'Fire', cat: 'Edgy', fn: frame('\ud83d\udd25', '\ud83d\udd25', mSB) },
  { name: 'Crown', cat: 'Decorative', fn: frame('\ud83d\udc51', '\ud83d\udc51', mB) },
  { name: 'Skull', cat: 'Edgy', fn: frame('\u2620', '\u2620', mBF) },
  { name: 'Gothic Cross', cat: 'Gothic', fn: frame('\u271e', '\u271e', mF) },
  { name: 'Moonlight', cat: 'Aesthetic', fn: frame('\u263e', '\u263d', mS) },
  { name: 'Cloud', cat: 'Aesthetic', fn: frame('\u2601\ufe0e', '\u2601\ufe0e', mBS) },
  { name: 'Music', cat: 'Fun', fn: frame('\u266a\u266b', '\u266b\u266a', mSI) },
  { name: 'Lightning', cat: 'Edgy', fn: frame('\u26a1', '\u26a1', mSB) },
  { name: 'Brackets', cat: 'Decorative', fn: frame('\u3010', '\u3011', mSB) },
  { name: 'Corner Quote', cat: 'Decorative', fn: frame('\u300c', '\u300d', mSans) },
  { name: 'Arrows', cat: 'Decorative', fn: frame('\u27a4', '\u25c4', mSB) },
  { name: 'Dashes', cat: 'Decorative', fn: frame('-=[', ']=-', mMono) },
  { name: 'Dot Frame', cat: 'Aesthetic', fn: frame('\u00b7\u02da \u0f18', '\u0f18 \u02da\u00b7', mS) },
  { name: 'Wave', cat: 'Aesthetic', fn: frame('\uff5e', '\uff5e', mFW) },
  { name: 'Snow', cat: 'Aesthetic', fn: frame('\u2744', '\u2744', mS) },
  { name: 'Ribbon', cat: 'Aesthetic', fn: frame('\ua4b0', '\ua4b1', mBS) },
  { name: 'Shine', cat: 'Decorative', fn: frame('\u2726', '\u2726', mB) },
  { name: 'Dot Separated', cat: 'Clean', fn: (t) => between(mSB(t), '\u00b7') },
  { name: 'Star Separated', cat: 'Decorative', fn: (t) => between(mB(t), '\u2726') },
  { name: 'Heart Separated', cat: 'Aesthetic', fn: (t) => between(mBS(t), '\u2661') },
  { name: 'Arrow Separated', cat: 'Decorative', fn: (t) => between(mSans(t), '\u27b3') },
  { name: 'Line Separated', cat: 'Clean', fn: (t) => between(mMono(t), '|') },
  { name: 'Spark Separated', cat: 'Aesthetic', fn: (t) => between(mS(t), '\u02da') },
  { name: 'Bold Strike', cat: 'Bold', fn: (t) => combine(mB(t), M.strike) },
  { name: 'Script Underline', cat: 'Script', fn: (t) => combine(mBS(t), M.underline) },
  { name: 'Gothic Strike', cat: 'Gothic', fn: (t) => combine(mF(t), M.strike) },
  { name: 'Outline Underline', cat: 'Clean', fn: (t) => combine(mDS(t), M.underline) },
  { name: 'Mono Strike', cat: 'Clean', fn: (t) => combine(mMono(t), M.strike) },
  { name: 'Wide Wavy', cat: 'Aesthetic', fn: (t) => combine(mFW(t), M.wavy) },
].map((s, i) => ({ ...s, id: `d${i}` }));
